"use client";

import React from "react";
import GenericCsvImportModal from "../GenericCsvImportModal";
import type {
  SimpleTableConfig,
  SimpleFieldConfig,
  BaseEntity,
  EntityHooks,
} from "../UniversalCRMTable/types";
import {
  generateFieldDescription,
  generateFieldValidation,
  generateSampleRows,
  transformFieldValue,
} from "./AutoImportModal.helpers";

interface AutoImportModalProps<T extends BaseEntity = BaseEntity> {
  config: SimpleTableConfig<T>;
  hooks: EntityHooks<T>;
  onSuccess?: () => void;
  triggerButton: React.ReactNode;
}

/**
 * Auto-configured import modal that generates CSV import functionality
 * from SimpleTableConfig and entity hooks automatically.
 */
const AutoImportModal = <T extends BaseEntity = BaseEntity>({
  config,
  hooks,
  onSuccess,
}: AutoImportModalProps<T>): React.ReactElement => {
  const triggerButtonRef = React.useRef<HTMLDivElement>(null);

  const columns = React.useMemo(() => {
    return Object.entries(config.fields)
      .filter(
        ([_, fieldConfig]: [string, SimpleFieldConfig]) =>
          !fieldConfig.formOnly && !fieldConfig.excludeFromApi && !fieldConfig.autoFill
      )
      .map(([fieldKey, fieldConfig]: [string, SimpleFieldConfig]) => ({
        key: fieldKey,
        label: fieldConfig.label,
        required: fieldConfig.required || false,
        description: generateFieldDescription(fieldConfig),
        validate: generateFieldValidation(fieldConfig),
      }));
  }, [config.fields]);

  const generateSampleCsv = React.useCallback(() => {
    const headers = columns.map((col) => col.key).join(",");
    const sampleRows = generateSampleRows(columns, config);
    return `${headers}\n${sampleRows}`;
  }, [columns, config]);

  const transformRowData = React.useCallback(
    (row: Record<string, any>) => {
      const transformed: Record<string, any> = {};

      columns.forEach(({ key }) => {
        const fieldConfig = config.fields[key];
        const value = row[key];

        if (value !== undefined && value !== null && value !== "") {
          transformed[key] = transformFieldValue(value, fieldConfig);
        }
      });

      Object.entries(config.fields).forEach(
        ([fieldKey, fieldConfig]: [string, SimpleFieldConfig]) => {
          if (fieldConfig.autoFill) {
            if (fieldKey.includes("created_at") || fieldKey.includes("updated_at")) {
              transformed[fieldKey] = new Date().toISOString();
            }
            if (fieldKey === "is_active") {
              transformed[fieldKey] = true;
            }
          }
        }
      );

      return transformed;
    },
    [columns, config.fields]
  );

  const createMutation = hooks.useCreate();
  const useCreateHook = React.useCallback(() => {
    return {
      mutateAsync: createMutation.mutateAsync,
      mutate: createMutation.mutateAsync,
      isPending: createMutation.isPending,
      isLoading: createMutation.isPending,
      isError: false,
      isSuccess: false,
      error: null,
      data: undefined,
      reset: () => {},
    } as any;
  }, [createMutation]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      triggerButtonRef.current?.click();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <GenericCsvImportModal
      modalTitle={`Bulk Import ${config.displayName}`}
      modalDescription={`Upload a CSV file to create multiple ${config.displayName.toLowerCase()} at once. Download the sample template to see the required format.`}
      columns={columns}
      generateSampleCsv={generateSampleCsv}
      useCreateHook={useCreateHook}
      transformRowData={transformRowData}
      onSuccess={onSuccess}
      triggerButton={<div ref={triggerButtonRef} style={{ display: "none" }} />}
    />
  );
};

export default AutoImportModal;
